/**
 * Member-portal design tokens — the single source for every colour, radius, shadow and font used by
 * the member screens.
 *
 * SUPERSEDES THE v1 PALETTE. The old system was built on `#1E9E54` green with Inter and 6px radii.
 * It is gone. These values are lifted verbatim from the visual reference
 * (`docs/assets/member-portal/reference/portal-visual-reference.html`, its `:root` block), which is
 * authoritative on look: deep forest green `#0E5F4C` on warm paper `#F7F5EF`, Inter for copy
 * and IBM Plex Mono for prices, dates and eyebrows, 18px cards and a two-layer shadow.
 *
 * These values also DELIBERATELY override the Boxfusion design system's `--bf-orange` (#FF6600).
 * The member portal is green: any member screen that shows orange chrome is wrong.
 *
 * Consume as `import { MEMBER_TOKENS as T } from "@/screens/member/tokens";` and reference
 * `T.primary` etc. rather than re-declaring hex values in a component — a token added here is the
 * only supported way to introduce a new colour.
 *
 * The two font stacks resolve through CSS custom properties that `src/app/layout.tsx` stamps on
 * `<html>` from `next/font/google`. The `var(…, fallback)` form is deliberate: under jsdom (and for
 * the one paint before the font loads) the variable is undefined and the quoted family name — or the
 * system stack behind it — takes over, so nothing ever falls back to an unstyled serif.
 */
export const MEMBER_TOKENS = {
    /* --------------------------------------------------------------------------------------------
     * Brand
     * ----------------------------------------------------------------------------------------- */

    /**
     * Primary buttons, the active nav pill, the hero panel, selected tier borders.
     *
     * **ANC green, and lighter than the forest green it replaces.** The previous `#0E5F4C` was a
     * dark, teal-leaning forest green that belonged to no brand in particular. PBF and PCF are ANC
     * programmes, so the primary is now the ANC/South African flag green — a truer, warmer green
     * that reads as the movement's rather than as a generic "corporate green".
     */
    primary: "#00843D",
    /** Hover/active state of anything `primary`, and text on a light green wash. */
    primaryHover: "#006B31",
    /** Alias of `primaryHover` for the cases where the colour is a shade rather than a state. */
    primaryDeep: "#005A29",
    /** Soft badges, discount chips, the emblem tile, hovered pills. */
    primaryTint: "#E6F3EB",
    /** Outline-button borders and any hairline that must read as green. */
    primaryLine: "#BFDECB",
    /** Text/glyph colour on a `primary` fill. */
    onPrimary: "#FFFFFF",
    /** Accent green for a checkmark or a status dot sitting ON the green hero. */
    onPrimaryAccent: "#7BE8A8",
    /**
     * The "Recommended" ribbon and nothing else — it is a highlight, not a status.
     *
     * Deepened ANC gold rather than the flag's `#FFB612`, which fails contrast as text on white and
     * is only safe as a fill. This value works as either.
     */
    gold: "#C9971B",

    /* --------------------------------------------------------------------------------------------
     * Type
     * ----------------------------------------------------------------------------------------- */

    /** `--ink`. Body copy. */
    ink: "#15211D",
    /** `--ink-soft`. Ledes, list items, secondary body copy — readable, just quieter than `ink`. */
    inkSoft: "#4A5651",
    /**
     * Headings and table column headers.
     *
     * Equal to `ink` by design: the reference sets every heading to `--ink`. The key is kept
     * separate because it carries a different intent, and because ~95 call sites say what they mean.
     */
    heading: "#15211D",
    /** `--ink-faint`. Meta copy, placeholders, disabled labels, the `/ month` beside a price. */
    muted: "#8A938E",

    /* --------------------------------------------------------------------------------------------
     * Surfaces and lines
     * ----------------------------------------------------------------------------------------- */

    /** `--paper`. The warm page background behind every card. */
    page: "#F7F5EF",
    /** `--card`. Card, panel and modal background. */
    surface: "#FFFFFF",
    /** Section-card header strips, read-only fields, inset panels. */
    surfaceAlt: "#FAF8F3",
    /** `--line`. Card and table borders. */
    hairline: "#E4E0D6",
    /** Internal dividers inside a card, where `hairline` reads too heavy. */
    hairlineSoft: "#EFEBE0",
    /** The unfilled part of a progress bar and the track behind a segmented control. */
    trackBg: "#EFEBE0",
    /** The translucent fill of the sticky topbar, sitting over `page`. */
    topbarBg: "rgba(247, 245, 239, 0.82)",
    /** Paired with `topbarBg` — without the blur the translucency reads as a rendering fault. */
    topbarBlur: "saturate(140%) blur(12px)",

    /* --------------------------------------------------------------------------------------------
     * Status
     * ----------------------------------------------------------------------------------------- */

    /** Paid / confirmed. */
    successBg: "#E6F4EC",
    successInk: "#127A4E",
    /** RSVP'd / informational callout (EFT banking details, documents received). */
    infoBg: "#E9EFFA",
    infoInk: "#3155A8",
    /** Waitlist / expiring membership / pending confirmation. */
    warnBg: "#FBF0E0",
    warnBorder: "#DFB877",
    warnInk: "#9A6B1E",
    /** Destructive text — validation errors, cancel-membership copy. */
    dangerBg: "#FBECEA",
    dangerInk: "#C0392B",

    /* --------------------------------------------------------------------------------------------
     * Shape
     * ----------------------------------------------------------------------------------------- */

    /** `--radius`. Cards, modals, tier cards, the hero panel. */
    radius: 18,
    /** Inputs, buttons and small controls. */
    radiusControl: 10,
    /** Discount chips, emblem tiles and other small square-ish decorations. */
    radiusChip: 8,
    /** Fully rounded: pill nav, status pills, count pills. */
    radiusPill: 9999,

    /** `--shadow`. The resting elevation of every card. */
    shadow: "0 1px 2px rgba(21, 33, 29, .04), 0 12px 30px -18px rgba(21, 33, 29, .25)",
    /** The lifted state of a card on hover. */
    shadowLift: "0 1px 2px rgba(21, 33, 29, .05), 0 26px 46px -22px rgba(21, 33, 29, .4)",
    /** The green hero panel, whose shadow is tinted by its own fill rather than by ink. */
    shadowHero: "0 20px 40px -24px rgba(0, 90, 41, .7)",

    /**
     * The hero panel's fill — **a single flat green, deliberately not a gradient.**
     *
     * It used to be a radial white highlight over a diagonal ramp from `#12735B` to `#0A4A3B`. That
     * is gone: the brief is flat colour in ANC green, and a gradient reads as decoration the brand
     * does not call for. Renamed from `heroGradient` because a token named "gradient" holding a flat
     * colour is a lie the next reader has to discover.
     *
     * Slightly lighter than `primary` so a hero sitting above a primary button still separates from
     * it, without either becoming a second brand colour.
     */
    heroFill: "#00914A",

    /* --------------------------------------------------------------------------------------------
     * Fonts
     * ----------------------------------------------------------------------------------------- */

    /** Inter. Everything that is prose. */
    fontFamily: 'var(--font-inter), Inter, system-ui, -apple-system, "Segoe UI", sans-serif',
    /** IBM Plex Mono. PRICES, DATES, eyebrows, reference numbers and the footer note — nothing else. */
    fontFamilyMono: 'var(--font-plex-mono), "IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace',

    /* --------------------------------------------------------------------------------------------
     * Layout
     *
     * ONE container width for every authenticated page, owned by `components/MemberPage`. No screen
     * declares its own — that is what produced the v1 mismatch between a full-width hero and a stack
     * of 820px-wide profile blocks. The two narrow widths below are narrow BY INTENT, for a form
     * that would be unreadable at the full container width.
     * ----------------------------------------------------------------------------------------- */

    /**
     * The shared page container.
     *
     * Widened from the reference's 1180 to 1440 so the card rails fit FIVE across instead of four.
     * The arithmetic that fixes the number: 1440 − (2 × 32) gutters = 1376 usable, and
     * 5 × 260 (`EVENT_CARD_WIDTH`) + 4 × 18 gaps = 1372. It fits with 4px to spare, which is why the
     * card width itself did not have to change.
     *
     * ⚠️ Anything that hard-codes a percentage minimum in a grid template will still force fewer
     * columns at this width regardless of the container — see the `minmax` notes in `BenefitsScreen`.
     */
    containerMaxWidth: 1440,
    /** Horizontal padding inside the container, at both the topbar and the page. */
    containerPaddingX: 32,
    /** The login / register / OTP card. */
    authMaxWidth: 480,
    /** The payment and invoice documents. */
    formMaxWidth: 560,
} as const;

export type MemberTokens = typeof MEMBER_TOKENS;
